/* eslint-disable roros/no-raw-html-in-routes */
// reason: pre-existing <button>/<input>/<select> elements that pre-date the LINT-1 (AILLM-3) rule extension. New code must use components/ui/*. Migration tracked separately.
import React, { useState } from 'react';
import { useOrgStore } from '../../stores/orgStore';
import { OrgService } from '../../services/OrgService';
import { useOrgMembers, useOrgInvites, useInviteMemberMutation, useUpdateMemberRoleMutation, useRemoveMemberMutation, useRevokeInviteMutation, useActiveOrgId } from '../../hooks/queries';
import { Input } from '../../components/ui/Input/Input';
import { Button } from '../../components/ui/Button/Button';
import { Select } from '../../components/ui/Select/Select';
import { MultiSelect } from '../../components/ui/MultiSelect/MultiSelect';
import { Badge } from '../../components/ui/Badge/Badge';
import { Table } from '../../components/ui/Table/Table';
import { TableActionButton, TableActions } from '../../components/ui';
import { UserMinus, X, RotateCw, Edit, KeyRound } from 'lucide-react';
import { Avatar } from '../../components/ui/Avatar/Avatar';
import { Modal } from '../../components/ui/Modal/Modal';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '../../components/ui/PageContainer/PageContainer';
import { StatCard } from '@/components/ui/StatCard';
import { PermissionGate } from '@/components/ui/PermissionGate';
import { PERMISSIONS } from '@/constants/permissions';
import { useNotificationStore } from '../../stores/notificationStore';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { formatDate, formatTime } from '@/lib/localeFormatters';
import { useQuery } from '@tanstack/react-query';
import { RolesService } from '@/services/RolesService';
import { EditMemberRolesModal } from '@/components/organization/EditMemberRolesModal';
import { SetMemberPasswordModal } from '@/components/organization/SetMemberPasswordModal';

interface MemberRow { 
  user_id: string; 
  email: string; 
  fullName?: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'invited' | 'suspended';
  joinedAt?: string;
  lastActiveAt?: string;
  roles?: Array<{ id: string; name: string; is_system: boolean }>;
}

interface InviteRow {
  id: string;
  email: string;
  role: 'admin' | 'member';
  status: 'pending' | 'accepted' | 'expired';
  sentAt: string;
  expiresAt: string;
  sentBy?: string;
}

interface ApiInviteRow {
  id: string;
  email: string;
  role: 'admin' | 'member';
  token: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export function MembersAndInvites() {
  usePageHeader({ title: t('Members & Invites'), breadcrumbs: [{ label: t('Organization'), path: '/organization' }] });

  const orgId = useActiveOrgId();
  const [email, setEmail] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ inviteLink?: string; emailSent?: boolean } | null>(null);
  const [activeTab, setActiveTab] = useState<'members' | 'invites'>('members');
  const [inviteStatusFilter, setInviteStatusFilter] = useState<'all' | 'pending' | 'accepted' | 'expired'>('all');
  const [editRolesModal, setEditRolesModal] = useState<{ 
    isOpen: boolean; 
    userId: string | null; 
    userEmail: string; 
    userName?: string;
  }>({ isOpen: false, userId: null, userEmail: '', userName: undefined });
  const [passwordModal, setPasswordModal] = useState<{
    isOpen: boolean;
    userId: string | null;
    userEmail: string;
    userName?: string;
  }>({ isOpen: false, userId: null, userEmail: '', userName: undefined });

  // Use React Query hooks
  const { data: rawMembers = [], isLoading: membersLoading } = useOrgMembers(orgId || undefined);
  const { data: rawInvites = [], isLoading: invitesLoading } = useOrgInvites(orgId || undefined);
  const inviteMutation = useInviteMemberMutation();
  const updateRoleMutation = useUpdateMemberRoleMutation();
  const removeMemberMutation = useRemoveMemberMutation();
  const revokeInviteMutation = useRevokeInviteMutation();

  // Fetch available roles
  const { data: availableRoles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['roles', orgId],
    queryFn: async () => {
      const rolesService = new RolesService();
      return rolesService.getRoles();
    },
    enabled: !!orgId,
  });

  const loading = membersLoading || invitesLoading || inviteMutation.isPending || updateRoleMutation.isPending || removeMemberMutation.isPending || revokeInviteMutation.isPending;

  // Map API data to component format
  const members: MemberRow[] = (rawMembers || []).map((member: any) => ({
    user_id: member.user_id,
    email: member.email,
    fullName: member.full_name,
    role: member.role,
    status: 'active', // All current members are active
    joinedAt: member.joined_at,
    roles: member.roles || []
  }));

  const invites: InviteRow[] = (rawInvites || []).map((invite: any) => ({
    id: invite.id,
    email: invite.email,
    role: invite.role,
    status: invite.accepted_at ? 'accepted' : (new Date(invite.expires_at) < new Date() ? 'expired' : 'pending'),
    sentAt: invite.created_at,
    expiresAt: invite.expires_at,
    sentBy: 'System' // We don't have sender info in current schema
  }));

  const handleInvite = async () => {
    if (!orgId || !email.trim() || selectedRoleIds.length === 0) return;
    try {
      const result = await inviteMutation.mutateAsync({
        orgId,
        email: email.trim().toLowerCase(),
        roleIds: selectedRoleIds
      });
      setEmail('');
      setSelectedRoleIds([]);
      setError(null);
      // Show invite link result (especially useful when email is not configured)
      setInviteResult({ inviteLink: result?.inviteLink, emailSent: result?.emailSent });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to invite');
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'owner' | 'admin' | 'member') => {
    if (!orgId) return;
    try {
      await updateRoleMutation.mutateAsync({ orgId, userId, role: newRole });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update role');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!orgId) return;
    try {
      await removeMemberMutation.mutateAsync({ orgId, userId });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove member');
    }
  };

  const handleResendInvite = async (inviteId: string) => {
    if (!orgId) return;
    try {
      const svc = new OrgService();
      await svc.resendInvite(orgId, inviteId);
      setError(null);
      // Show success message via notification system
      try { useNotificationStore.getState().success(t('Invite resent successfully')); } catch {}
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to resend invite');
      try { useNotificationStore.getState().error(t('Failed to resend invite')); } catch {}
    }
  };

  const handleRemoveInvite = async (inviteId: string) => {
    if (!orgId) return;
    try {
      await revokeInviteMutation.mutateAsync({ orgId, inviteId });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke invite');
    }
  };

  const memberColumns = [
    {
      key: 'user',
      title: t('Member'),
      sortable: true,
      render: (_: any, record: MemberRow) => (
        <div className="flex items-center gap-3">
          <Avatar 
            size="sm" 
            initials={record.fullName ? record.fullName.charAt(0).toUpperCase() : record.email.charAt(0).toUpperCase()}
            name={record.fullName || record.email}
          />
          <div>
            <div className="font-medium text-foreground">{record.fullName || record.email}</div>
            {record.fullName && (
              <div className="text-sm text-muted-foreground">{record.email}</div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'role',
      title: t('Role'),
      sortable: true,
      render: (value: string, record: MemberRow) => (
        <div className="flex items-center gap-2">
          <Badge variant={value === 'owner' ? 'secondary' : value === 'admin' ? 'secondary' : 'outline'}>
            {value === 'owner' ? t('Owner') : value === 'admin' ? t('Admin') : t('Member')}
          </Badge>
        </div>
      )
    },
    {
      key: 'status',
      title: t('Status'),
      sortable: true,
      render: (value: string) => (
        <Badge variant={value === 'active' ? 'success' : value === 'invited' ? 'warning' : 'error'}>
          {value === 'active' ? t('Active') : value === 'invited' ? t('Invited') : t('Suspended')}
        </Badge>
      )
    },
    {
      key: 'joinedAt',
      title: t('Joined'),
      sortable: true,
      render: (value: string) => {
        if (!value) return <span className="text-muted-foreground">—</span>;
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{formatDate(date)}</div>
            <div className="text-xs text-muted-foreground">{formatTime(date)}</div>
          </div>
        );
      }
    },
    {
      key: 'roles',
      title: t('Roles'),
      render: (_: any, record: MemberRow) => (
        <div className="flex flex-wrap gap-1">
          {record.roles && record.roles.length > 0 ? (
            record.roles.map((role) => (
              <Badge 
                key={role.id} 
                variant={role.is_system ? 'primary' : 'secondary'}
              >
                {role.name}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">No roles assigned</span>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      title: t('Actions'),
      align: 'right' as const,
      render: (_: any, record: MemberRow) => (
        <PermissionGate
          anyPermissions={[PERMISSIONS.MANAGE_USERS, PERMISSIONS.EDIT_USER_ROLES]}
          fallback={null}
        >
          <div className="flex items-center justify-end gap-2">
            <TableActionButton
              variant="default"
              icon={Edit}
              onClick={() => setEditRolesModal({
                isOpen: true,
                userId: record.user_id,
                userEmail: record.email,
                userName: record.fullName
              })}
            >
              Edit Roles
            </TableActionButton>
            {record.role !== 'owner' && (
              <>
                <TableActionButton
                  variant="default"
                  icon={KeyRound}
                  onClick={() => setPasswordModal({
                    isOpen: true,
                    userId: record.user_id,
                    userEmail: record.email,
                    userName: record.fullName
                  })}
                >
                  Set Password
                </TableActionButton>
                <TableActionButton
                  variant="destructive"
                  icon={UserMinus}
                  onClick={() => handleRemoveMember(record.user_id)}
                >
                  Remove
                </TableActionButton>
              </>
            )}
          </div>
        </PermissionGate>
      )
    }
  ];

  const inviteColumns = [
    {
      key: 'email',
      title: t('Email'),
      sortable: true
    },
    {
      key: 'role',
      title: t('Role'),
      render: (value: string) => (
        <Badge variant={value === 'admin' ? 'secondary' : 'outline'}>
          {value === 'admin' ? t('Admin') : t('Member')}
        </Badge>
      )
    },
    {
      key: 'status',
      title: t('Status'),
      render: (value: string) => (
        <Badge variant={value === 'accepted' ? 'success' : value === 'expired' ? 'error' : 'warning'}>
          {value === 'accepted' ? t('Accepted') : value === 'expired' ? t('Expired') : t('Pending')}
        </Badge>
      )
    },
    {
      key: 'sentAt',
      title: t('Sent'),
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{formatDate(date)}</div>
            <div className="text-xs text-muted-foreground">{formatTime(date)}</div>
          </div>
        );
      }
    },
    {
      key: 'expiresAt',
      title: t('Expires'),
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        const isExpired = date < new Date();
        return (
          <div className={isExpired ? 'text-error-600' : ''}>
            <div className="text-sm">{formatDate(date)}</div>
            <div className="text-xs text-muted-foreground">{formatTime(date)}</div>
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: t('Actions'),
      align: 'right' as const,
      render: (_: any, record: InviteRow) => (
        <TableActions>
          <TableActionButton
            icon={RotateCw}
            onClick={() => handleResendInvite(record.id)}
            disabled={record.status !== 'pending'}
          >
            Resend
          </TableActionButton>
          <TableActionButton
            variant="destructive"
            icon={X}
            onClick={() => handleRemoveInvite(record.id)}
          >
            Cancel
          </TableActionButton>
        </TableActions>
      )
    }
  ];

  if (!orgId) {
    return (
      <div className="p-8">
        <div className="text-center">
          <div className="text-sm text-muted-foreground">No organization selected</div>
        </div>
      </div>
    );
  }

  return (
    <PageContainer>
      <div className="flex items-center justify-end mb-4 gap-2">
        <PermissionGate anyPermissions={[PERMISSIONS.MANAGE_USERS, PERMISSIONS.INVITE_USERS]}>
          <Button
            size="sm"
            onClick={() => setShowInviteModal(true)}
            leftIcon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            }
          >
            Invite Member
          </Button>
        </PermissionGate>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg">
          <div className="text-sm text-error-600">{error}</div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label={t('Total Members')}
          value={members.length}
          iconColor="primary"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 110 7.75M8 3.13a4 4 0 110 7.75" />
            </svg>
          }
        />

        <StatCard
          label={t('Pending Invites')}
          value={invites.filter(i => i.status === 'pending').length}
          iconColor="warning"
          valueColor={invites.filter(i => i.status === 'pending').length > 0 ? "warning" : "default"}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        <StatCard
          label={t('Admins')}
          value={members.filter(m => m.role === 'admin' || m.role === 'owner').length}
          iconColor="default"
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
          }
        />
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-border">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('members')}
              className={cn(
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'members'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              Members ({members.length})
            </button>
            <button
              onClick={() => setActiveTab('invites')}
              className={cn(
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'invites'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              Invites ({invites.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'members' ? (
        <Table
          data={members}
          columns={memberColumns}
          loading={loading}
          searchPlaceholder="Search members..."
          emptyText="No members found."
        />
      ) : (
        <div>
          <div className="mb-4 flex items-center gap-2">
            <label className="text-sm font-medium text-foreground">Status:</label>
            <select
              value={inviteStatusFilter}
              onChange={e => setInviteStatusFilter(e.target.value as typeof inviteStatusFilter)}
              className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All ({invites.length})</option>
              <option value="pending">Pending ({invites.filter(i => i.status === 'pending').length})</option>
              <option value="accepted">Accepted ({invites.filter(i => i.status === 'accepted').length})</option>
              <option value="expired">Expired ({invites.filter(i => i.status === 'expired').length})</option>
            </select>
          </div>
          <Table
            data={inviteStatusFilter === 'all' ? invites : invites.filter(i => i.status === inviteStatusFilter)}
            columns={inviteColumns}
            loading={loading}
            searchPlaceholder="Search invites..."
            emptyText={inviteStatusFilter === 'all' ? 'No invites.' : `No ${inviteStatusFilter} invites.`}
          />
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <Modal
          isOpen={showInviteModal}
          onClose={() => { setShowInviteModal(false); setInviteResult(null); }}
          title={inviteResult ? 'Invitation Created' : 'Invite Team Member'}
          size="md"
        >
          {inviteResult ? (
            <div className="space-y-4">
              {inviteResult.emailSent ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Invitation email sent
                  </div>
                  <p className="text-sm text-green-600">The invitation email has been sent. They have 7 days to accept.</p>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    Email not configured
                  </div>
                  <p className="text-sm text-amber-600">No email provider is set up. Share the invite link manually.</p>
                </div>
              )}

              {inviteResult.inviteLink && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Invite Link</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={inviteResult.inviteLink}
                      className="flex-1 px-3 py-2 text-sm bg-muted border border-border rounded-lg text-muted-foreground font-mono truncate"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(inviteResult.inviteLink!);
                      }}
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy
                    </Button>
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">Share this link with the invitee. It expires in 7 days.</p>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button onClick={() => { setShowInviteModal(false); setInviteResult(null); }}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter email address"
                  required
                />
              </div>

              <div>
                <MultiSelect
                  label="Roles"
                  placeholder="Select one or more roles..."
                  value={selectedRoleIds}
                  onChange={setSelectedRoleIds}
                  options={availableRoles.map(role => ({
                    value: role.id,
                    label: role.name,
                    description: role.description || undefined,
                  }))}
                  error={selectedRoleIds.length === 0 && error ? 'At least one role is required' : undefined}
                  required
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Users can have multiple roles. Permissions from all roles will be combined.
                </p>
              </div>

              <div className="bg-muted rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">What will happen next?</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• An invitation will be created for {email || 'the provided email'}</li>
                  <li>• They'll have 7 days to accept the invitation</li>
                  <li>• You can resend or cancel the invitation anytime</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleInvite}
                  disabled={!email.trim() || selectedRoleIds.length === 0 || inviteMutation.isPending || rolesLoading}
                  isLoading={inviteMutation.isPending}
                  className="flex-1"
                >
                  Send Invitation
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviteMutation.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Edit Member Roles Modal */}
      {editRolesModal.isOpen && editRolesModal.userId && orgId && (
        <EditMemberRolesModal
          isOpen={editRolesModal.isOpen}
          onClose={() => setEditRolesModal({ isOpen: false, userId: null, userEmail: '', userName: undefined })}
          orgId={orgId}
          userId={editRolesModal.userId}
          userEmail={editRolesModal.userEmail}
          userName={editRolesModal.userName}
        />
      )}

      {/* Set Member Password Modal */}
      {passwordModal.isOpen && passwordModal.userId && orgId && (
        <SetMemberPasswordModal
          isOpen={passwordModal.isOpen}
          onClose={() => setPasswordModal({ isOpen: false, userId: null, userEmail: '', userName: undefined })}
          orgId={orgId}
          userId={passwordModal.userId}
          userEmail={passwordModal.userEmail}
          userName={passwordModal.userName}
        />
      )}
    </PageContainer>
  );
}
